import React, { useState, useEffect } from 'react';
import { Buildings, User, Lock, Phone, IdentificationCard, MapPin, ArrowLeft, ArrowRight, CheckCircle } from '@phosphor-icons/react/ssr';
import { GoogleLogin } from '@react-oauth/google';
import { API_BASE_URL } from './services/apiClient';

// ── Constantes ────────────────────────────────────────────────────────────────

const STEPS_NORMAL = [
  { id: 1, label: 'Tipo de cuenta' },
  { id: 2, label: 'Credenciales' },
  { id: 3, label: 'Datos de empresa' },
];

const STEPS_GOOGLE = [
  { id: 1, label: 'Tipo de cuenta' },
  { id: 3, label: 'Datos de empresa' },
];

const initialForm = {
  isIndividual:    false,
  companyName:     '',
  taxId:           '',
  contactName:     '',
  phone:           '',
  city:            '',
  country:         '',
  username:        '',
  email:           '',
  password:        '',
  confirmPassword: '',
  // Campos de Google (vacíos si es registro normal)
  googleId:        '',
  fullName:        '',
  avatarUrl:       '',
};

// ── Componente principal ──────────────────────────────────────────────────────

const RegisterPage = ({ onNavigate, theme }) => {
  const [step, setStep]         = useState(1);
  const [form, setForm]         = useState(initialForm);
  const [errors, setErrors]     = useState({});
  const [loading, setLoading]   = useState(false);
  const [success, setSuccess]   = useState(null); // { tenantSlug, tenantName }
  const [googleMode, setGoogleMode] = useState(false);
  const [storedIdToken, setStoredIdToken] = useState('');

  // Leer datos de Google guardados por LoginPage cuando el usuario no estaba registrado
  useEffect(() => {
    const raw = sessionStorage.getItem('googleRegData');
    if (!raw) return;
    try {
      const data = JSON.parse(raw);
      const emailPart = (data.email || '').split('@')[0].replace(/[^a-z0-9._\-]/g, '').toLowerCase();
      setForm(prev => ({
        ...prev,
        email:       data.email  || '',
        contactName: data.name   || '',
        username:    emailPart   || '',
        googleId:    data.googleId || '',
        fullName:    data.name   || '',
      }));
      setStoredIdToken(data.idToken || '');
      setGoogleMode(true);
      setStep(3); // Saltar directo a datos de empresa
      sessionStorage.removeItem('googleRegData');
    } catch { /* ignorar datos corruptos */ }
  }, []);

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  // ── Validaciones por paso ──────────────────────────────────────────────────

  const validateStep1 = () => true;

  const validateStep2 = () => {
    const e = {};
    if (!form.username || form.username.length < 3)
      e.username = 'El usuario debe tener al menos 3 caracteres.';
    if (!/^[a-z0-9._\-]+$/.test(form.username))
      e.username = 'Solo letras minúsculas, números, puntos y guiones.';
    if (!form.email || !form.email.includes('@'))
      e.email = 'Ingresa un correo electrónico válido.';
    if (!form.password || form.password.length < 8)
      e.password = 'La contraseña debe tener al menos 8 caracteres.';
    if (form.password !== form.confirmPassword)
      e.confirmPassword = 'Las contraseñas no coinciden.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep3 = () => {
    const e = {};
    if (!form.companyName.trim())
      e.companyName = form.isIndividual
        ? 'Tu nombre completo es requerido.'
        : 'El nombre de la empresa es requerido.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ── Navegación entre pasos ─────────────────────────────────────────────────

  const goNext = () => {
    if (googleMode) {
      // Modo Google: solo hay paso 1 y paso 3
      if (step === 1 && validateStep1()) setStep(3);
      return;
    }
    const valid = step === 1 ? validateStep1()
                : step === 2 ? validateStep2()
                : true;
    if (valid) setStep(s => s + 1);
  };

  const goBack = () => {
    setErrors({});
    if (googleMode && step === 3) {
      setStep(1);
      return;
    }
    setStep(s => s - 1);
  };

  // ── Manejador de Google Sign-In en el paso 1 ──────────────────────────────

  const handleGoogleInRegister = async (credentialResponse) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL()}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken: credentialResponse.credential })
      });
      const data = await res.json();

      if (res.ok) {
        // Usuario ya registrado — redirigir a login con mensaje
        onNavigate?.('login');
        return;
      }

      if (res.status === 404 && data.canRegister) {
        // Nuevo usuario — pre-llenar datos y saltar a paso 3
        const emailPart = (data.email || '').split('@')[0].replace(/[^a-z0-9._\-]/g, '').toLowerCase();
        setForm(prev => ({
          ...prev,
          email:       data.email  || '',
          contactName: data.name   || '',
          username:    emailPart   || '',
          googleId:    data.googleId || '',
          fullName:    data.name   || '',
        }));
        setStoredIdToken(credentialResponse.credential);
        setGoogleMode(true);
        setStep(3);
      } else {
        setErrors({ submit: data.message || 'Error al conectar con Google.' });
      }
    } catch {
      setErrors({ submit: 'Error de conexión al verificar cuenta de Google.' });
    } finally {
      setLoading(false);
    }
  };

  // ── Envío ─────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!validateStep3()) return;

    setLoading(true);
    try {
      const body = {
        username:    form.username.trim().toLowerCase(),
        email:       form.email.trim().toLowerCase(),
        isIndividual: form.isIndividual,
        companyName: form.companyName.trim(),
        taxId:       form.taxId.trim() || null,
        contactName: form.contactName.trim() || null,
        phone:       form.phone.trim() || null,
        city:        form.city.trim() || null,
        country:     form.country.trim() || null,
      };

      if (googleMode) {
        // Registro con Google: sin contraseña
        body.googleId  = form.googleId;
        body.fullName  = form.fullName || null;
        body.avatarUrl = form.avatarUrl || null;
      } else {
        body.password = form.password;
      }

      const res = await fetch(`${API_BASE_URL()}/api/auth/register`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (res.ok) {
        const data = await res.json();

        if (googleMode && storedIdToken) {
          // Auto-login con el id_token de Google
          const loginRes = await fetch(`${API_BASE_URL()}/api/auth/google`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ idToken: storedIdToken })
          });
          if (loginRes.ok) {
            const loginData = await loginRes.json();
            localStorage.setItem('authToken', loginData.token);
            localStorage.setItem('refreshToken', loginData.refreshToken);
            // Guardar user completo para que AuthProvider lo lea al volver al login
            localStorage.setItem('user', JSON.stringify({
              username:           loginData.user.username,
              role:               loginData.user.role,
              company:            loginData.user.company,
              tenantId:           loginData.user.tenantId,
              isProfileComplete:  loginData.user.isProfileComplete ?? true,
              mustChangePassword: loginData.user.mustChangePassword ?? false,
              permissions:        loginData.user.permissions
            }));
            sessionStorage.setItem('pendingLogin', 'true');
          }
        }

        setSuccess({ tenantSlug: data.tenantSlug, tenantName: data.tenantName });
      } else {
        const err = await res.json().catch(() => ({}));
        setErrors({ submit: err.detail || err.title || 'Error al crear la cuenta. Intenta de nuevo.' });
      }
    } catch {
      setErrors({ submit: 'Error de conexión. Verifica tu internet e intenta de nuevo.' });
    } finally {
      setLoading(false);
    }
  };

  // ── Pantalla de éxito ─────────────────────────────────────────────────────

  if (success) {
    const hasPendingLogin = sessionStorage.getItem('pendingLogin') === 'true';
    return (
      <PublicShell theme={theme}>
        <div className="text-center space-y-4">
          <CheckCircle size={56} className="mx-auto text-green-500" weight="fill" />
          <h2 className="text-2xl font-bold text-white">¡Cuenta creada!</h2>
          <p className="text-white/70">
            Tu empresa <strong className="text-white">{success.tenantName}</strong> ya está
            registrada en la plataforma.
          </p>
          {hasPendingLogin ? (
            <p className="text-white/60 text-sm">
              Tu sesión está lista. Haz clic para entrar.
            </p>
          ) : (
            <p className="text-white/60 text-sm">
              Inicia sesión para comenzar a usar GEOCheck.
            </p>
          )}
          <button
            onClick={() => {
              sessionStorage.removeItem('pendingLogin');
              onNavigate('login');
            }}
            className="w-full mt-4 py-3 rounded-xl font-semibold text-white transition-all"
            style={{ background: 'var(--primary)' }}
          >
            {hasPendingLogin ? 'Entrar a la plataforma' : 'Ir al inicio de sesión'}
          </button>
        </div>
      </PublicShell>
    );
  }

  // ── Formulario ────────────────────────────────────────────────────────────

  const steps = googleMode ? STEPS_GOOGLE : STEPS_NORMAL;

  // Índice visual del paso actual para el indicador
  const stepIndex = googleMode
    ? (step === 1 ? 1 : 2)
    : step;

  return (
    <PublicShell theme={theme}>
      {/* Logo y título */}
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-white">
          {googleMode ? 'Completa tu registro' : 'Crear cuenta'}
        </h2>
        <p className="text-white/60 text-sm mt-1">Plataforma de validación GDB</p>
      </div>

      {/* Indicador de pasos */}
      <StepIndicator steps={steps} currentStep={stepIndex} />

      {/* Contenido por paso */}
      <div className="mt-6 space-y-4">
        {step === 1 && (
          <>
            <Step1
              form={form}
              set={set}
              onGoogleSuccess={handleGoogleInRegister}
              onGoogleError={() => setErrors({ submit: 'Error al iniciar Google Sign-In.' })}
            />
            {loading && (
              <p className="text-white/60 text-sm text-center">Verificando cuenta de Google…</p>
            )}
          </>
        )}
        {step === 2 && !googleMode && <Step2 form={form} set={set} errors={errors} />}
        {step === 3 && (
          <>
            {googleMode && (
              <div className="flex items-center gap-2 mb-2 p-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.08)' }}>
                <span className="text-white/60 text-xs">Registrando con Google:</span>
                <span className="text-white text-xs font-medium">{form.email}</span>
              </div>
            )}
            <Step3 form={form} set={set} errors={errors} googleMode={googleMode} />
          </>
        )}

        {errors.submit && (
          <p className="text-red-300 text-sm text-center bg-red-900/30 rounded-lg p-3">
            {errors.submit}
          </p>
        )}
      </div>

      {/* Navegación */}
      <div className="flex gap-3 mt-6">
        {step > 1 ? (
          <button
            onClick={goBack}
            className="flex-1 py-3 rounded-xl font-medium text-white/80 border border-white/20 hover:bg-white/10 transition-all flex items-center justify-center gap-2"
          >
            <ArrowLeft size={16} /> Atrás
          </button>
        ) : (
          <button
            onClick={() => onNavigate('login')}
            className="flex-1 py-3 rounded-xl font-medium text-white/80 border border-white/20 hover:bg-white/10 transition-all flex items-center justify-center gap-2"
          >
            <ArrowLeft size={16} /> Ya tengo cuenta
          </button>
        )}

        {step === 3 ? (
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 py-3 rounded-xl font-semibold text-white transition-all disabled:opacity-60"
            style={{ background: 'var(--primary)' }}
          >
            {loading ? 'Creando cuenta…' : 'Crear cuenta'}
          </button>
        ) : step < (googleMode ? 3 : 3) ? (
          <button
            onClick={goNext}
            className="flex-1 py-3 rounded-xl font-semibold text-white transition-all flex items-center justify-center gap-2"
            style={{ background: 'var(--primary)' }}
          >
            Siguiente <ArrowRight size={16} />
          </button>
        ) : null}
      </div>
    </PublicShell>
  );
};

// ── Paso 1 — Tipo de cuenta ───────────────────────────────────────────────────

const Step1 = ({ form, set, onGoogleSuccess, onGoogleError }) => (
  <div className="space-y-3">
    <p className="text-white/70 text-sm text-center mb-2">¿Cómo quieres registrarte?</p>

    <button
      onClick={() => set('isIndividual', false)}
      className={`w-full p-4 rounded-xl border text-left transition-all flex items-center gap-4 ${
        !form.isIndividual
          ? 'border-[var(--primary)] bg-[var(--primary)]/20'
          : 'border-white/20 hover:border-white/40'
      }`}
    >
      <Buildings size={28} className="text-white shrink-0" weight={!form.isIndividual ? 'fill' : 'regular'} />
      <div>
        <p className="text-white font-medium">Represento una empresa</p>
        <p className="text-white/50 text-xs mt-0.5">Tendrás un espacio para tu organización</p>
      </div>
    </button>

    <button
      onClick={() => set('isIndividual', true)}
      className={`w-full p-4 rounded-xl border text-left transition-all flex items-center gap-4 ${
        form.isIndividual
          ? 'border-[var(--primary)] bg-[var(--primary)]/20'
          : 'border-white/20 hover:border-white/40'
      }`}
    >
      <User size={28} className="text-white shrink-0" weight={form.isIndividual ? 'fill' : 'regular'} />
      <div>
        <p className="text-white font-medium">Soy persona natural / freelancer</p>
        <p className="text-white/50 text-xs mt-0.5">Cuenta individual sin empresa</p>
      </div>
    </button>

    {/* Divisor */}
    <div className="flex items-center gap-3 py-1">
      <div className="flex-1 h-px bg-white/20" />
      <span className="text-white/40 text-xs">o</span>
      <div className="flex-1 h-px bg-white/20" />
    </div>

    {/* Botón de Google */}
    <div className="flex justify-center">
      <GoogleLogin
        onSuccess={onGoogleSuccess}
        onError={onGoogleError}
        text="signup_with"
        locale="es"
        width="100%"
      />
    </div>
  </div>
);

// ── Paso 2 — Credenciales ─────────────────────────────────────────────────────

const Step2 = ({ form, set, errors }) => (
  <div className="space-y-3">
    <AuthInput
      icon={User}
      placeholder="Nombre de usuario (ej: juan.perez)"
      value={form.username}
      onChange={e => set('username', e.target.value.toLowerCase())}
      error={errors.username}
      autoComplete="username"
    />
    <AuthInput
      icon={IdentificationCard}
      type="email"
      placeholder="Correo electrónico"
      value={form.email}
      onChange={e => set('email', e.target.value)}
      error={errors.email}
      autoComplete="email"
    />
    <AuthInput
      icon={Lock}
      type="password"
      placeholder="Contraseña (mínimo 8 caracteres)"
      value={form.password}
      onChange={e => set('password', e.target.value)}
      error={errors.password}
      autoComplete="new-password"
    />
    <AuthInput
      icon={Lock}
      type="password"
      placeholder="Confirmar contraseña"
      value={form.confirmPassword}
      onChange={e => set('confirmPassword', e.target.value)}
      error={errors.confirmPassword}
      autoComplete="new-password"
    />
  </div>
);

// ── Paso 3 — Datos de empresa / persona ───────────────────────────────────────

const Step3 = ({ form, set, errors, googleMode }) => (
  <div className="space-y-3">
    {/* En modo Google mostrar username editable */}
    {googleMode && (
      <AuthInput
        icon={User}
        placeholder="Nombre de usuario (ej: juan.perez)"
        value={form.username}
        onChange={e => set('username', e.target.value.toLowerCase())}
        error={errors.username}
        autoComplete="username"
      />
    )}
    <AuthInput
      icon={form.isIndividual ? User : Buildings}
      placeholder={form.isIndividual ? 'Tu nombre completo *' : 'Nombre de la empresa *'}
      value={form.companyName}
      onChange={e => set('companyName', e.target.value)}
      error={errors.companyName}
    />
    <AuthInput
      icon={User}
      placeholder="Nombre del contacto principal"
      value={form.contactName}
      onChange={e => set('contactName', e.target.value)}
    />
    <AuthInput
      icon={Phone}
      placeholder="Teléfono de contacto"
      value={form.phone}
      onChange={e => set('phone', e.target.value)}
    />
    {!form.isIndividual && (
      <AuthInput
        icon={IdentificationCard}
        placeholder="NIT / RUT (opcional)"
        value={form.taxId}
        onChange={e => set('taxId', e.target.value)}
      />
    )}
    <AuthInput
      icon={MapPin}
      placeholder="Ciudad"
      value={form.city}
      onChange={e => set('city', e.target.value)}
    />
    <AuthInput
      icon={MapPin}
      placeholder="País"
      value={form.country}
      onChange={e => set('country', e.target.value)}
    />
    <p className="text-white/40 text-xs text-center">
      Los campos sin * son opcionales y se pueden completar después en tu perfil.
    </p>
  </div>
);

// ── Sub-componentes ───────────────────────────────────────────────────────────

const AuthInput = ({ icon: Icon, type = 'text', placeholder, value, onChange, error, autoComplete }) => (
  <div>
    <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all ${
      error ? 'border-red-400 bg-red-900/20' : 'border-white/20 bg-white/10 focus-within:border-white/50'
    }`}>
      {Icon && <Icon size={18} className="text-white/50 shrink-0" />}
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        className="flex-1 bg-transparent text-white placeholder:text-white/40 text-sm outline-none"
      />
    </div>
    {error && <p className="text-red-300 text-xs mt-1 pl-1">{error}</p>}
  </div>
);

const StepIndicator = ({ steps, currentStep }) => (
  <div className="flex items-center justify-center gap-2">
    {steps.map((s, i) => (
      <React.Fragment key={s.id}>
        <div className={`flex items-center gap-1.5 ${currentStep === i + 1 ? 'text-white' : 'text-white/40'}`}>
          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
            currentStep > i + 1
              ? 'bg-green-500 text-white'
              : currentStep === i + 1
              ? 'bg-white text-gray-900'
              : 'border border-white/30 text-white/40'
          }`}>
            {currentStep > i + 1 ? '✓' : i + 1}
          </div>
          <span className="hidden sm:block text-xs">{s.label}</span>
        </div>
        {i < steps.length - 1 && (
          <div className={`flex-1 max-w-8 h-px ${currentStep > i + 1 ? 'bg-green-500' : 'bg-white/20'}`} />
        )}
      </React.Fragment>
    ))}
  </div>
);

const PublicShell = ({ children, theme }) => (
  <div
    className="min-h-screen flex items-center justify-center p-4"
    style={{ background: 'linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)' }}
  >
    <div className="w-full max-w-md">
      {/* Logo */}
      {theme?.logo && (
        <div className="flex justify-center mb-6">
          <img src={theme.logo} alt="Logo" className="h-14 object-contain" />
        </div>
      )}
      <div className="rounded-2xl p-8" style={{ background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(12px)' }}>
        {children}
      </div>
    </div>
  </div>
);

export default RegisterPage;
